import type { FC } from 'react'

import { CWButton } from '@features/common.components/ComponentsWeb/Button'
import {
  CWHeading,
  CWHeadingHeader,
} from '@features/common.components/ComponentsWeb/Heading'
import { MarkdownRenderer } from '@features/common.components/MarkdownRenderer'
import { ParameterSection } from '@features/playground.components/ParameterSection'
import { Response } from '@features/playground.components/Response'
import { ResponseSelection } from '@features/playground.components/ResponseSelection'
import Image from 'next/image'
import useTranslation from 'next-translate/useTranslation'

import { OperationTemplate } from '../OperationTemplate'

import {
  getExampleParametersObject,
  getRequestBodyProperties,
  getTransformedRequestBodyProperties,
} from './helpers'

import type { Operation } from '@features/playground.graphql'

export const dynamic = 'force-dynamic'

export interface ApiReferencePageProps {
  /**
   * The operation details.
   */
  operation: Operation
  /**
   * Callback to handle the play button click.
   */
  handlePlayClick: () => void
}

export const ApiReferencePage: FC<ApiReferencePageProps> = ({
  operation,
  handlePlayClick,
}) => {
  const { t } = useTranslation('playground')

  const requestBodyProperties = getRequestBodyProperties(operation)
  const transformedRequestBodyProperties =
    getTransformedRequestBodyProperties(operation)

  return (
    <div className="flex w-full flex-1 flex-col space-y-4 px-6 pb-6 pt-4">
      <div className="flex w-full flex-1 flex-col space-y-4 md:flex-row">
        <div className="border-neutral-subtle pr-4 md:w-1/2 md:border-r">
          <div className="pb-6">
            <div className="flex items-start">
              <MarkdownRenderer
                className="mt-4 text-neutral type-body-base [&_p]:type-body-base"
                markdown={operation.description}
              />
              <span className="mt-4 min-w-fit">
                <CWButton
                  className="ml-2 normal-case"
                  label={t('play')}
                  onClick={handlePlayClick}
                  size="sm"
                  variant="secondary"
                >
                  <Image
                    alt={t('play')}
                    className="pr-1"
                    height={24}
                    src="/customSVG/SolidPlay.svg"
                    width={24}
                  />
                  <span>{t('play')}</span>
                </CWButton>
              </span>
            </div>
          </div>
          <div className="space-y-4">
            <ParameterSection
              parameters={operation.parameters.header}
              title={t`parameter.titles.header`}
            />
            <ParameterSection
              parameters={operation.parameters.path}
              title={t`parameter.titles.path`}
            />
            <ParameterSection
              parameters={operation.parameters.query}
              title={t`parameter.titles.query`}
            />
            <ParameterSection
              parameters={transformedRequestBodyProperties}
              title={t`parameter.titles.requestBody`}
            />
          </div>
          <CWHeading className="mb-4 mt-7" variant="section/md">
            <CWHeadingHeader>{t`parameter.responseTitle`}</CWHeadingHeader>
          </CWHeading>
          <div className="space-y-4">
            {operation.responses.map((response) => (
              <Response key={response.statusCode} {...response} />
            ))}
          </div>
        </div>
        <div className="flex h-full flex-col space-y-6 p-4 md:w-1/2">
          <OperationTemplate
            header={{
              title: t`request.title`,
              description: t`request.description`,
            }}
            input={{
              identifier: operation.slug,
              parameters: {
                cookies: getExampleParametersObject(
                  operation.parameters.cookies,
                ),
                header: getExampleParametersObject(operation.parameters.header),
                path: getExampleParametersObject(operation.parameters.path),
                query: getExampleParametersObject(operation.parameters.query),
              },
              requestBody: getExampleParametersObject(requestBodyProperties),
            }}
          />
          <div className="max-h-full">
            {operation.responses[0]?.codeSnippet != null ? (
              <ResponseSelection items={operation.responses} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
